/**
 * LocationIntelligence , 4-component Location Intelligence section for the
 * Opportunity detail page.
 *
 *   ┌────────────────────────────┬─────────────────────┐
 *   │                            │  Street View        │
 *   │   PinMap (satellite)       │  (Component 2)      │
 *   │   (Component 1)            ├─────────────────────┤
 *   │                            │  Nearby Amenities   │
 *   │                            │  (Component 3)      │
 *   ├────────────────────────────┴─────────────────────┤
 *   │       Nearby Opportunities in Pipeline           │
 *   │       (Component 4 , 0.5mi radius overlay)       │
 *   └──────────────────────────────────────────────────┘
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { APIProvider } from "@vis.gl/react-google-maps";
import {
  GraduationCap, ShoppingCart, Hospital, TrainFront, UtensilsCrossed,
  Maximize2, X, MapPin,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  GMAPS_KEY, GMAPS_LIBS, isMapsKeyConfigured, fetchNearbyAmenities, geocodeAddress,
} from "./gmaps";
import { PinMap, StreetViewPanel } from "./LocationPreview";
import { fmtCurrencyFull } from "@/lib/format";

const RADIUS_MILES = 0.5;
const METERS_PER_MILE = 1609.344;
const AMENITY_ICONS = {
  schools: GraduationCap,
  grocery: ShoppingCart,
  hospitals: Hospital,
  transit: TrainFront,
  restaurants: UtensilsCrossed,
};

export default function LocationIntelligence({ opp, onCoordsChange }) {
  const [coords, setCoords] = useState(
    Number.isFinite(opp?.lat) && Number.isFinite(opp?.lng) ? { lat: opp.lat, lng: opp.lng } : null,
  );
  const [mapType, setMapType] = useState("hybrid"); // Component 1: satellite default
  const [fullscreen, setFullscreen] = useState(false);

  // Auto-geocode once on mount if the opp has no coords yet
  useEffect(() => {
    if (coords || !isMapsKeyConfigured()) return;
    if (!opp?.address) return;
    let cancelled = false;
    // Wait for the SDK to load (APIProvider mounts it inside <MapsBlock> below)
    const tick = setInterval(async () => {
      if (!window.google?.maps?.Geocoder) return;
      clearInterval(tick);
      try {
        const r = await geocodeAddress({
          address: opp.address, city: opp.city, state: opp.state, zip_code: opp.zip_code,
        });
        if (cancelled) return;
        setCoords({ lat: r.lat, lng: r.lng });
        onCoordsChange?.({ lat: r.lat, lng: r.lng });
      } catch {
        toast.message("Address could not be geocoded. Map unavailable.");
      }
    }, 200);
    return () => { cancelled = true; clearInterval(tick); };
  }, [opp, coords, onCoordsChange]);

  if (!isMapsKeyConfigured()) {
    return (
      <Section>
        <div className="text-xs text-amber-300">
          Google Maps key not configured. Set REACT_APP_GOOGLE_MAPS_API_KEY to enable this section.
        </div>
      </Section>
    );
  }

  return (
    <Section data-testid="location-intelligence">
      <APIProvider apiKey={GMAPS_KEY} libraries={GMAPS_LIBS}>
        {/* Top row: Pin Map + Street View, side-by-side, same height */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="relative">
            <PinMap
              height={420}
              coords={coords}
              mapType={mapType}
              onMapType={setMapType}
              onPinDrag={(ll) => { setCoords(ll); onCoordsChange?.(ll); }}
              showFullscreen={false}
              defaultZoom={17}
              gestureHandling="cooperative"
              testid="opp-pin-map"
              placeholderMessage="Geocoding the property address…"
            />
            {coords && (
              <button
                type="button"
                onClick={() => setFullscreen(true)}
                data-testid="opp-pin-map-fullscreen"
                className="absolute bottom-3 right-3 z-10 bg-white/95 text-slate-900 text-[11px] font-semibold inline-flex items-center gap-1 px-2.5 py-1.5 rounded-sm shadow hover:bg-white"
              >
                <Maximize2 className="h-3 w-3" /> Fullscreen
              </button>
            )}
          </div>
          <StreetViewPanel height={420} coords={coords} pegmanEnabled={true} testid="opp-street-view" />
        </div>

        {/* Bottom row: Nearby Amenities + Nearby Opportunities, side-by-side */}
        <div className="grid md:grid-cols-2 gap-4">
          <NearbyAmenities coords={coords} />
          <ComparablesOverlay coords={coords} oid={opp.oid} />
        </div>
      </APIProvider>

      {fullscreen && coords && (
        <FullscreenMap coords={coords} mapType={mapType} setMapType={setMapType} onClose={() => setFullscreen(false)} />
      )}
    </Section>
  );
}

function Section({ children, ...rest }) {
  return (
    <section className="ae-card p-5 space-y-4" {...rest}>
      <div className="flex items-center gap-2">
        <MapPin className="h-3.5 w-3.5 text-emerald-400" />
        <div className="text-[10px] uppercase tracking-widest text-slate-500">Location Intelligence</div>
      </div>
      {children}
    </section>
  );
}

// =====================================================================
// Component 3: Nearby Amenities
// =====================================================================

function NearbyAmenities({ coords }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Cache the lookup in component state, explicitly per spec (no re-fetch on every render).
  // Only mark a cacheKey as "fetched" once we have at least one non-empty category;
  // an all-empty result usually means the SDK wasn't ready yet, so we retry on next render.
  const cacheKey = coords ? `${coords.lat.toFixed(4)},${coords.lng.toFixed(4)}` : null;
  const fetchedKeyRef = useRef(null);

  useEffect(() => {
    if (!coords || !cacheKey || fetchedKeyRef.current === cacheKey) return;
    // Wait until the Google JS SDK is fully loaded before issuing the Place call,
    // otherwise importLibrary("places") rejects and every category returns empty.
    if (!window.google?.maps?.importLibrary) {
      const t = setTimeout(() => fetchedKeyRef.current = null /* trigger retry */, 800);
      return () => clearTimeout(t);
    }
    setBusy(true);
    setErr(null);
    fetchNearbyAmenities({ lat: coords.lat, lng: coords.lng })
      .then((result) => {
        const anyHit = Object.values(result).some((cat) => cat.count > 0);
        setData(result);
        // Only memoize the cacheKey once we got at least one hit. If the
        // location truly has zero amenities we'll just keep re-rendering with
        // the empty result, which is cheap.
        if (anyHit) fetchedKeyRef.current = cacheKey;
      })
      .catch((e) => setErr(e.message || "Failed to load nearby amenities"))
      .finally(() => setBusy(false));
  }, [coords, cacheKey]);

  return (
    <div className="ae-card p-4 space-y-2" data-testid="nearby-amenities">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">Nearby Amenities</div>
      {!coords && <div className="text-xs text-slate-500">Awaiting coordinates</div>}
      {coords && busy && <div className="text-xs text-slate-500">Searching the neighborhood…</div>}
      {coords && err && <div className="text-xs text-amber-300">{err}</div>}
      {coords && data && (
        <div className="space-y-2 max-h-[340px] overflow-y-auto pr-1">
          {Object.entries(data).map(([key, cat]) => {
            const Icon = AMENITY_ICONS[key] || MapPin;
            return (
              <div key={key} data-testid={`amenity-cat-${key}`}>
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-emerald-400">
                  <Icon className="h-3 w-3" /> {cat.label} <span className="text-slate-500">({cat.count})</span>
                </div>
                {cat.items.length === 0 ? (
                  <div className="text-[11px] text-slate-500 ml-4">None within radius</div>
                ) : (
                  <ul className="mt-1 ml-4 space-y-0.5">
                    {cat.items.map((i) => (
                      <li key={i.place_id} className="text-[12px] flex justify-between gap-2">
                        <span className="text-slate-200 truncate">{i.name}</span>
                        <span className="text-slate-500 font-mono text-[10px] whitespace-nowrap">{i.distance_miles}mi</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// =====================================================================
// Component 4: Nearby Opportunities in Pipeline (radius overlay)
// =====================================================================

function ComparablesOverlay({ coords, oid }) {
  const [comps, setComps] = useState({ items: [], total: 0 });
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    if (!coords) return;
    api.get("/maps/comparables", { params: {
      lat: coords.lat, lng: coords.lng, radius_miles: RADIUS_MILES, exclude_oid: oid,
    }}).then((r) => setComps(r.data)).catch(() => setComps({ items: [], total: 0 }));
  }, [coords, oid]);

  const pins = useMemo(() => comps.items.map((c) => ({
    key: c.oid, lat: c.lat, lng: c.lng,
    title: `${c.title} , ${c.asset_type} , ${fmtCurrencyFull(c.asking_price || 0)}`,
  })), [comps]);

  return (
    <div className="ae-card p-4 space-y-2" data-testid="comparables-overlay">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">Nearby Opportunities in Pipeline</div>
        <div className="text-[11px] text-emerald-400" data-testid="comparables-count">
          {comps.total} within {RADIUS_MILES} mi
        </div>
      </div>
      {coords ? (
        <PinMap
          height={240}
          coords={coords}
          mapType="roadmap"
          defaultZoom={15}
          pins={pins}
          circle={{ center: coords, radiusMeters: RADIUS_MILES * METERS_PER_MILE }}
          gestureHandling="cooperative"
          testid="comparables-map"
        />
      ) : (
        <div className="ae-card flex items-center justify-center text-xs text-slate-500" style={{ height: 240 }}>
          Awaiting coordinates
        </div>
      )}
      {comps.total === 0 && coords && (
        <div className="text-xs text-slate-500 italic">No pipeline opportunities within {RADIUS_MILES} miles.</div>
      )}
      {comps.total > 0 && (
        <ul className="text-[11px] space-y-1 pt-1 max-h-[90px] overflow-y-auto pr-1" data-testid="comparables-list">
          {comps.items.map((c) => (
            <li
              key={c.oid}
              onMouseEnter={() => setHovered(c.oid)}
              onMouseLeave={() => setHovered(null)}
              className={`px-2 py-1 rounded-sm border border-white/8 transition ${hovered === c.oid ? "border-emerald-500/40 bg-emerald-500/5" : ""}`}
              data-testid={`comp-row-${c.oid}`}
            >
              <div className="flex justify-between gap-2">
                <span className="font-medium text-slate-200 truncate">{c.title}</span>
                <span className="text-emerald-400 font-mono whitespace-nowrap">{c.distance_miles}mi</span>
              </div>
              <div className="text-[10px] text-slate-500 flex gap-2 capitalize">
                <span>{c.asset_type}</span>
                <span>{fmtCurrencyFull(c.asking_price || 0)}</span>
                {c.cf_readiness_score != null && <span>CF {c.cf_readiness_score}</span>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// =====================================================================
// Fullscreen modal
// =====================================================================

function FullscreenMap({ coords, mapType, setMapType, onClose }) {
  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-[5vh] bg-black/85" data-testid="opp-pin-fullscreen-modal">
      <div className="relative w-[90vw] h-[90vh] bg-[#05070A] border border-white/10 rounded-sm overflow-hidden">
        <button
          onClick={onClose}
          data-testid="opp-pin-fullscreen-close"
          className="absolute top-3 right-3 z-20 bg-white/95 text-slate-900 inline-flex items-center justify-center h-8 w-8 rounded-sm shadow hover:bg-white"
        >
          <X className="h-4 w-4" />
        </button>
        <APIProvider apiKey={GMAPS_KEY} libraries={GMAPS_LIBS}>
          <PinMap
            height={"100%"}
            coords={coords}
            mapType={mapType}
            onMapType={setMapType}
            defaultZoom={18}
            gestureHandling="greedy"
            testid="opp-pin-map-fullscreen"
          />
        </APIProvider>
      </div>
    </div>
  );
}
